import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatDate, formatEuro } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Receipt, Plus, Trash2, Loader2, Printer, Save } from "lucide-react";
import { toast } from "sonner";

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

function printFattura(f, inte) {
  const head = inte
    ? `<div style="border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:24px">
         <div style="font-size:18px;font-weight:700">${inte.nome}</div>
         <div style="font-size:12px;color:#444">${[inte.indirizzo, inte.citta].filter(Boolean).join(" · ")}</div>
         ${inte.partita_iva ? `<div style="font-size:12px;color:#444">P.IVA ${inte.partita_iva}</div>` : ""}
       </div>` : "";
  const rows = (f.voci || []).map((v) => `<tr><td style="padding:6px;border:1px solid #999">${v.descrizione || ""}</td><td style="padding:6px;border:1px solid #999;text-align:right">${formatEuro(v.importo)}</td></tr>`).join("");
  const conto = f.conto || null;
  const contoHtml = conto && (conto.iban || conto.banca || conto.intestatario)
    ? `<div style="margin-top:20px;padding-top:12px;border-top:1px solid #ccc;font-size:13px;color:#222">
         <div style="font-weight:700;margin-bottom:4px">Coordinate per il pagamento${conto.nome ? ` — ${conto.nome}` : ""}</div>
         ${conto.intestatario ? `<div>Intestatario: ${conto.intestatario}</div>` : ""}
         ${conto.banca ? `<div>Banca: ${conto.banca}${conto.filiale ? ` — ${conto.filiale}` : ""}</div>` : ""}
         ${conto.iban ? `<div>IBAN: ${conto.iban}</div>` : ""}
         ${conto.bic_swift ? `<div>BIC/SWIFT: ${conto.bic_swift}</div>` : ""}
       </div>` : "";
  const w = window.open("", "_blank");
  w.document.write(`<html><head><title>Fattura ${f.numero}</title><meta charset="utf-8"/>
    <style>body{font-family:Georgia,serif;color:#111;padding:48px;line-height:1.6}table{border-collapse:collapse;width:100%;margin-top:16px}</style>
    </head><body>${head}
    <h2 style="margin:0">Fattura / Parcella n. ${f.numero}</h2>
    <div style="color:#444;font-size:13px">Data: ${formatDate(f.data)}</div>
    <div style="margin-top:12px"><strong>Intestatario:</strong> ${f.intestatario || ""}</div>
    <table><thead><tr><th style="padding:6px;border:1px solid #999;text-align:left">Descrizione</th><th style="padding:6px;border:1px solid #999;text-align:right">Importo</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <div style="margin-top:16px;text-align:right">
      <div>Imponibile: <strong>${formatEuro(f.imponibile)}</strong></div>
      <div>IVA ${f.aliquota_iva || 0}%: <strong>${formatEuro(f.iva)}</strong></div>
      <div style="font-size:18px;margin-top:6px">TOTALE: <strong>${formatEuro(f.totale)}</strong></div>
    </div>
    ${contoHtml}
    ${f.note ? `<div style="margin-top:16px;color:#444">${f.note}</div>` : ""}
    </body></html>`);
  w.document.close(); w.focus();
  setTimeout(() => w.print(), 300);
}

export default function FatturaDialog({ open, onOpenChange, pratica }) {
  const [voci, setVoci] = useState([]);
  const [intestatario, setIntestatario] = useState("");
  const [aliquota, setAliquota] = useState("22");
  const [note, setNote] = useState("");
  const [fatture, setFatture] = useState([]);
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contoIdx, setContoIdx] = useState(0);

  const base = `/studio/pratiche/${pratica?.id}`;

  const load = async () => {
    setLoading(true);
    try {
      const [c, f, s] = await Promise.all([
        api.get(`${base}/costi`),
        api.get(`${base}/fatture`),
        api.get("/studio/me"),
      ]);
      setVoci((c.data || []).map((x) => ({ descrizione: x.voce, importo: String(x.importo ?? "") })));
      setFatture(f.data || []);
      setStudio(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open) {
      setIntestatario(pratica?.assistito_ragione_sociale || pratica?.nome_assistito || "");
      setAliquota("22"); setNote("");
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const imponibile = voci.reduce((s, v) => s + num(v.importo), 0);
  const iva = Math.round(imponibile * num(aliquota)) / 100;
  const totale = Math.round((imponibile + iva) * 100) / 100;

  const setVoce = (i, k, val) => setVoci((arr) => arr.map((v, idx) => (idx === i ? { ...v, [k]: val } : v)));
  const addVoce = () => setVoci((arr) => [...arr, { descrizione: "", importo: "" }]);
  const delVoce = (i) => setVoci((arr) => arr.filter((_, idx) => idx !== i));

  const inteObj = studio ? {
    nome: (studio.settings || {}).intestazione || studio.studio_name || "",
    indirizzo: (studio.settings || {}).indirizzo || "", citta: (studio.settings || {}).citta || "",
    partita_iva: (studio.settings || {}).partita_iva || "",
  } : null;

  const conti = ((studio?.settings || {}).conti_correnti || []).filter((c) => c && (c.iban || c.banca || c.nome));
  useEffect(() => {
    const di = conti.findIndex((c) => c.predefinito);
    setContoIdx(di >= 0 ? di : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studio]);

  const emit = async () => {
    const payload = {
      intestatario: intestatario.trim(),
      aliquota_iva: num(aliquota),
      note: note.trim(),
      conto: conti[contoIdx] || null,
      voci: voci.filter((v) => v.descrizione.trim() || num(v.importo)).map((v) => ({ descrizione: v.descrizione.trim(), importo: num(v.importo) })),
    };
    if (payload.voci.length === 0) { toast.error("Aggiungi almeno una voce"); return; }
    setSaving(true);
    try {
      await api.post(`${base}/fatture`, payload);
      toast.success("Fattura emessa e registrata in Contabilità");
      const f = await api.get(`${base}/fatture`);
      setFatture(f.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const delFattura = async (f) => {
    if (!window.confirm(`Eliminare la fattura n. ${f.numero}?`)) return;
    try { await api.delete(`/studio/fatture/${f.id}`); setFatture((x) => x.filter((i) => i.id !== f.id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="fattura-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <Receipt className="h-5 w-5 text-amber-400" /> Fattura Assistito
            <span className="text-sm font-normal text-slate-500">· {pratica?.numero_pratica}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
        ) : (
          <>
            <div className="grid sm:grid-cols-[1fr_120px] gap-3">
              <div>
                <Label className="text-slate-300 text-xs">Intestatario</Label>
                <Input value={intestatario} onChange={(e) => setIntestatario(e.target.value)} data-testid="fattura-intestatario" className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Aliquota IVA %</Label>
                <Input type="number" step="1" value={aliquota} onChange={(e) => setAliquota(e.target.value)} data-testid="fattura-iva" className="mt-1 h-9 bg-slate-900/60 border-slate-700 font-mono" />
              </div>
            </div>

            <div className="mt-1">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-slate-300 text-xs">Voci (precompilate dai costi della pratica)</Label>
                <button onClick={addVoce} data-testid="fattura-add-voce" className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300"><Plus className="h-3.5 w-3.5" /> Voce</button>
              </div>
              <div className="space-y-2">
                {voci.length === 0 && <div className="text-sm text-slate-500 py-2">Nessuna voce. Aggiungine una.</div>}
                {voci.map((v, i) => (
                  <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center" data-testid={`fattura-voce-${i}`}>
                    <Input value={v.descrizione} onChange={(e) => setVoce(i, "descrizione", e.target.value)} placeholder="Descrizione" className="h-9 bg-slate-900/60 border-slate-700" />
                    <Input type="number" step="0.01" value={v.importo} onChange={(e) => setVoce(i, "importo", e.target.value)} placeholder="€" className="h-9 bg-slate-900/60 border-slate-700 font-mono" />
                    <button onClick={() => delVoce(i)} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-slate-300 text-xs">Note</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="fattura-note" className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
            </div>

            {conti.length > 0 && (
              <div data-testid="fattura-conto-wrap">
                <Label className="text-slate-300 text-xs">Conto corrente (coordinate riportate in fattura)</Label>
                <select value={contoIdx} onChange={(e) => setContoIdx(Number(e.target.value))} data-testid="fattura-conto" className="mt-1 h-9 w-full rounded-md bg-slate-900/60 border border-slate-700 px-2 text-sm text-slate-100">
                  {conti.map((c, i) => (
                    <option key={i} value={i} className="bg-slate-900 text-slate-100">{(c.nome || `Conto ${i + 1}`)}{c.predefinito ? " (predefinito)" : ""}{c.iban ? ` — ${c.iban}` : (c.banca ? ` — ${c.banca}` : "")}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-center justify-between border-t border-slate-800 pt-3 text-sm">
              <div className="text-slate-400 font-mono space-y-0.5">
                <div>Imponibile: <span className="text-slate-200">{formatEuro(imponibile)}</span></div>
                <div>IVA: <span className="text-slate-200">{formatEuro(iva)}</span></div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Totale</div>
                <div className="text-xl font-head font-extrabold text-emerald-400 font-mono" data-testid="fattura-totale">{formatEuro(totale)}</div>
              </div>
            </div>

            <Button onClick={emit} disabled={saving} data-testid="fattura-emit" className="w-full bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Emetti fattura</>}
            </Button>

            <div className="border-t border-slate-800 pt-3">
              <div className="text-xs uppercase tracking-widest text-cyan-400 font-mono font-bold mb-2">Fatture emesse per la pratica</div>
              {fatture.length === 0 ? (
                <div className="text-sm text-slate-500 py-2">Nessuna fattura emessa.</div>
              ) : (
                <div className="divide-y divide-slate-800">
                  {fatture.map((f) => (
                    <div key={f.id} className="flex items-center gap-3 py-2.5" data-testid={`fattura-${f.id}`}>
                      <Receipt className="h-5 w-5 text-amber-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-100">Fattura n. {f.numero} · {f.intestatario}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{formatDate(f.data)}</div>
                      </div>
                      <span className="text-sm font-mono text-emerald-400">{formatEuro(f.totale)}</span>
                      <button onClick={() => printFattura(f, inteObj)} data-testid={`fattura-print-${f.id}`} title="Stampa" className="text-slate-300 hover:text-cyan-400"><Printer className="h-4 w-4" /></button>
                      <button onClick={() => delFattura(f)} data-testid={`fattura-del-${f.id}`} title="Elimina" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
